import {
    Component,
    ChangeDetectionStrategy,
    AfterViewInit,
} from "@angular/core";
import { first, Observable } from "rxjs";
import { Store } from "@ngrx/store";
import moment from "moment";

import { ValidatableComponent } from "../components/base.component";
import { PersistenceService } from "../store/model/persistence.service";
import { RootState } from "../store/root";
import { stateBuilder } from "../store/utils";
import { AttendantsValidity } from "../store/validity/state";
import {
    Protocol,
    Participant,
    ConsultationProcess,
    ConsultationDate,
    Customer,
    Person,
    Agent,
    Individual,
} from "../data/model";
import { isIndividual, isCompany } from "../data/model-utils";
import { ZASConstants } from "../data/model.constants";
import { KeycloakService } from "../security";
import { UserInfo } from "../data/product";

@Component({
    selector: "zbd-attendants",
    templateUrl: "attendant.component.html",
    styleUrls: ["../components/snippets.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttendantComponent
    extends ValidatableComponent
    implements AfterViewInit
{
    state$: Observable<LocalState>;
    validity$: Observable<AttendantsValidity>;

    moment = moment;
    ZASConstants = ZASConstants;
    getKeys = Object.keys;
    salesTypes: string[];

    constructor(
        public persistence: PersistenceService,
        store: Store<RootState>,
        keycloak: KeycloakService,
    ) {
        super();
        this.state$ = LocalState.create(store);
        this.validity$ = store.select<AttendantsValidity>(
            (s) => s.validity.attendants,
        );

        const userInfo = new UserInfo(keycloak.getExistingParsedToken());
        this.salesTypes = Object.keys(ZASConstants.SalesTypeTable).filter(
            (salesType) => {
                return (
                    salesType !== "internet" &&
                    (!userInfo.isDa() || salesType !== "personal")
                );
            },
        );
    }

    ngAfterViewInit(): void {
        this.state$.pipe(first()).subscribe((state) => {
            if (
                !state.protocol.CreatorName &&
                (state.agent.LastName || state.agent.AgencyName)
            ) {
                this.fillAgentName(state.agent, state.protocol);
            }
        });
    }

    public addParticipant(protocol: Protocol) {
        return () => {
            this.persistence.addProtocol_Participants(
                protocol,
                {} as Participant,
            );
        };
    }

    public addConsultationDate(cp: ConsultationProcess) {
        return () => {
            this.persistence.addConsultationProcess_ConsultationDates(cp, {
                Date: moment().format("YYYY-MM-DD"),
            } as any as ConsultationDate);
        };
    }

    public getCustomerName(customer: Customer) {
        if (isIndividual(customer)) {
            return this.getPersonName(customer);
        } else if (isCompany(customer)) {
            return customer.ContactName;
        } else {
            return null;
        }
    }

    public getPersonName(person: Person | Individual) {
        if (person.FirstName && person.LastName) {
            return `${person.FirstName} ${person.LastName}`;
        } else if (person.LastName) {
            return person.LastName;
        } else {
            return null;
        }
    }

    public fillAgentName(agent: Agent, protocol: Protocol) {
        this.persistence.updateProtocol(
            protocol,
            "CreatorName",
            this.getPersonName(agent) || agent.AgencyName,
            true,
        );
    }

    setNameAttributes(name: string, person: Person) {
        if (name && person) {
            const nameparts = name.split(" ");
            if (nameparts.length > 1) {
                person.FirstName = nameparts[0].trim();
                nameparts.splice(0, 1);
                person.LastName = nameparts.join(" ").trim();
            } else if (nameparts.length === 1) {
                person.FirstName = "";
                person.LastName = nameparts[0].trim();
            } else {
                person.FirstName = "";
                person.LastName = "";
            }
        }
    }
}

class LocalState {
    customer: Customer;
    agent: Agent;
    protocol: Protocol;
    consultationProcess: ConsultationProcess;

    static create(store: Store<RootState>): Observable<LocalState> {
        return stateBuilder(store, new LocalState())
            .apply("customer", (s) => s.model.ConsultationProcess.Customer)
            .apply("agent", (s) => s.model.ConsultationProcess.Agent)
            .apply("protocol", (s) => s.model.ConsultationProcess.Protocol)
            .apply("consultationProcess", (s) => s.model.ConsultationProcess)
            .state$;
    }
}
